import { useEffect, useMemo, useState, useCallback, useRef } from "react";
import { useTranslation, Trans } from "react-i18next";
import { UAParser } from "ua-parser-js";
import LocationLoading from "../../reusable/LocationLoading";
import LocationError from "../../reusable/LocationError";
import { validateProcess } from "../../../store/verification.slice";
import { useSelector } from "react-redux";

// GPS is an enrichment, not a gate. core's LocationIntelligence activity reads a
// null location as LOW accuracy and falls back to the IP lookup, and no
// restriction check reads the GPS fix at all — so refusing or failing to get a
// position must never end the verification. Every branch below offers a way on.
const STAGE_CHECKING = "checking";
const STAGE_ASK = "ask";
const STAGE_LOCATING = "locating";
const STAGE_BLOCKED = "blocked";
const STAGE_RESOLVED = "resolved";

const PRECISE_OPTIONS = { enableHighAccuracy: true, timeout: 10000, maximumAge: 60000 };
const COARSE_OPTIONS = { enableHighAccuracy: false, timeout: 8000, maximumAge: 300000 };

const DeviceData = ({ inputChange, field, verification }) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [validationError, setValidationError] = useState("");
  const [softRestrictionError, setSoftRestrictionError] = useState("");
  const [hardRestrictionError, setHardRestrictionError] = useState("");
  const [pendingDeviceData, setPendingDeviceData] = useState(null);
  const [stage, setStage] = useState(STAGE_CHECKING);
  const [blockedReason, setBlockedReason] = useState("denied");
  const { credentials } = useSelector((store) => store.verification);

  // Held in a ref rather than state so handleDeviceData keeps a stable identity:
  // the permission bootstrap effect depends on it, and a new identity each
  // render would re-run that effect and re-submit.
  const locationRef = useRef(null);
  const bootstrappedRef = useRef(false);

  const highAccurracyCheck = useMemo(() => {
    return verification?.settings.reduce(
      (acc, setting) => {
        if (setting.fieldName === "HIGH_ACCURACY_CHECK") {
          acc.highAccurracyCheck = setting;
        }
        return acc;
      },
      { highAccurracyCheck: null }
    ).highAccurracyCheck;
  }, [verification.settings]);

  const osName = useMemo(() => {
    return new UAParser(navigator.userAgent).getOS().name || "";
  }, []);

  const locationDeniedDesc = useMemo(() => {
    const isIOS = /iOS/i.test(osName);
    const isAndroid = /Android/i.test(osName);
    if (isIOS) {
      return (
        <span>
          {t("deviceData.locationDenied.iosBlocked")}
          <br />
          <br />
          <Trans i18nKey="deviceData.locationDenied.iosStep1" components={{ strong: <strong/> }} />
          <br />
          <Trans i18nKey="deviceData.locationDenied.iosStep2" components={{ strong: <strong/> }} />
          <br />
          <Trans i18nKey="deviceData.locationDenied.iosStep3" components={{ strong: <strong/> }} />
        </span>
      );
    }
    if (isAndroid) {
      return (
        <span>
          {t("deviceData.locationDenied.androidBlocked")}
          <br />
          <br />
          <Trans i18nKey="deviceData.locationDenied.androidStep1" components={{ strong: <strong/> }} />
          <br />
          <Trans i18nKey="deviceData.locationDenied.androidStep2" components={{ strong: <strong/> }} />
          <br />
          <Trans i18nKey="deviceData.locationDenied.androidStep3" components={{ strong: <strong/> }} />
        </span>
      );
    }
    return t("deviceData.locationDenied.fallback");
  }, [osName, t]);

  const getDeviceData = () => navigator.userAgent;

  const getCurrentTimeAndTimeZone = () => {
    const options = {
      timeZoneName: "short",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: true,
    };

    const formatter = new Intl.DateTimeFormat("en-US", options);
    const parts = formatter.formatToParts(new Date());
    const timeZoneAbbreviation =
      parts.find((part) => part.type === "timeZoneName")?.value || "N/A";

    const fullTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

    return {
      currentTime: new Date(),
      timeZoneAbbreviation,
      fullTimeZone,
    };
  };

  const handleDeviceData = useCallback(async () => {
    setValidationError("");
    setSoftRestrictionError("");
    setHardRestrictionError("");
    setPendingDeviceData(null);
    setStage(STAGE_RESOLVED);
    setLoading(true);

    const search = window.location.search;
    const params = new URLSearchParams(search);

    const data = getDeviceData();
    const result = new UAParser(data).getResult();

    const timeZone = getCurrentTimeAndTimeZone();
    const deviceData = {
      fieldName: field,
      fileName: null,
      value: JSON.stringify({
        browser: result.browser,
        cpu: result.cpu,
        device: result.device,
        operatingSystem: result.os,
        browserEngine: result.engine,
        userAgent: result.ua,
        ipAddress: verification.ipAddress,
        location: locationRef.current,
        source: "browser",
        timeZone: {
          abbr: timeZone.timeZoneAbbreviation,
          id: timeZone.fullTimeZone,
          currentTime: timeZone.currentTime,
        },
      }),
    };

    try {
      await validateProcess(
        "LOCATION_INTELLIGENCE",
        [],
        [deviceData],
        params.get("clientId"),
        verification?.verificationId,
        credentials?.accessToken
      );

      inputChange([{ document: null, field: deviceData }]);
    } catch (err) {
      if (err.response?.data) {
        const errors = err.response.data;
        const softError = errors.find(
          (e) => e?.errorCode === "LOCATION_SOFT_RESTRICTION"
        );
        const hardError = errors.find(
          (e) => e?.errorCode === "LOCATION_RESTRICTION"
        );
        const vpnError = errors.find(
          (e) => e?.errorCode === "VPN_RESTRICTION"
        );
        if (softError) {
          setSoftRestrictionError(softError.defaultMessage);
          setPendingDeviceData(deviceData);
        } else if (hardError) {
          setHardRestrictionError(hardError.defaultMessage);
        } else if (vpnError) {
          setValidationError(vpnError.defaultMessage);
        } else {
          const validationErr = errors
            .map((error) => error?.defaultMessage)
            .join("\n");
          setValidationError(validationErr);
        }
      } else {
        console.error("Device data validation failed", err);
        setValidationError("An unexpected error occurred.");
      }
    } finally {
      setLoading(false);
    }
  }, [field, verification, credentials, inputChange]);

  const proceedWithoutLocation = useCallback(() => {
    locationRef.current = null;
    handleDeviceData();
  }, [handleDeviceData]);

  const requestLocation = useCallback(() => {
    if (!navigator.geolocation) {
      console.warn("Geolocation is not supported, continuing with IP-based location");
      proceedWithoutLocation();
      return;
    }

    setStage(STAGE_LOCATING);

    // A high-accuracy fix regularly times out indoors on iOS, so a failed
    // precise attempt retries once coarsely before we give up on GPS.
    const attempt = (precise) => {
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          locationRef.current = {
            latitude: pos.coords.latitude,
            longitude: pos.coords.longitude,
            accuracy: pos.coords.accuracy,
          };
          handleDeviceData();
        },
        (err) => {
          if (err.code === err.PERMISSION_DENIED) {
            console.warn("Geolocation permission denied", err);
            setBlockedReason("denied");
            setStage(STAGE_BLOCKED);
            return;
          }
          if (precise) {
            attempt(false);
            return;
          }
          console.warn("Geolocation unavailable", err);
          setBlockedReason("unavailable");
          setStage(STAGE_BLOCKED);
        },
        precise ? PRECISE_OPTIONS : COARSE_OPTIONS
      );
    };

    attempt(true);
  }, [handleDeviceData, proceedWithoutLocation]);

  // A permission re-enabled in OS settings only takes effect after a reload, so
  // the blocked screen reloads instead of re-querying a still-denied permission.
  const reloadPage = () => window.location.reload();

  useEffect(() => {
    if (bootstrappedRef.current) return;
    bootstrappedRef.current = true;

    if (highAccurracyCheck?.value !== "true") {
      handleDeviceData();
      return;
    }

    // Without the Permissions API (in-app webviews, older WebKit) the state
    // cannot be read up front, so ask directly rather than rendering nothing.
    if (!navigator.permissions?.query) {
      setStage(STAGE_ASK);
      return;
    }

    navigator.permissions
      .query({ name: "geolocation" })
      .then((result) => {
        if (result.state === "granted") {
          // Must still fetch the fix: submitting here would send a null
          // location and record LOW accuracy for a user who said yes.
          requestLocation();
        } else if (result.state === "denied") {
          setBlockedReason("denied");
          setStage(STAGE_BLOCKED);
        } else {
          setStage(STAGE_ASK);
        }
      })
      .catch((err) => {
        console.warn("Geolocation permission query failed", err);
        setStage(STAGE_ASK);
      });
  }, [handleDeviceData, highAccurracyCheck, requestLocation]);

  const handleContinueAnyway = useCallback(() => {
    if (pendingDeviceData) {
      inputChange([{ document: null, field: pendingDeviceData }]);
    }
  }, [pendingDeviceData, inputChange]);

  const busy = loading || stage === STAGE_CHECKING || stage === STAGE_LOCATING;
  const hasError = validationError || softRestrictionError || hardRestrictionError;

  return (
    <div className="flex flex-col h-full">
      {busy && !hasError && <LocationLoading />}

      {validationError && (
        <LocationError
          tryAgain={handleDeviceData}
          desc={validationError}
          buttonText={t("deviceData.errors.tryAgain")}
        />
      )}

      {softRestrictionError && (
        <LocationError
          desc={t("deviceData.errors.softRestriction")}
          onContinue={handleContinueAnyway}
        />
      )}

      {hardRestrictionError && (
        <LocationError
          desc={t("deviceData.errors.hardRestriction")}
        />
      )}

      {!busy && stage === STAGE_ASK && (
        <LocationError
          desc={t("deviceData.errors.locationPromptNeeded")}
          buttonText={t("deviceData.errors.allowLocationAccess")}
          tryAgain={requestLocation}
          onContinue={proceedWithoutLocation}
          continueText={t("deviceData.errors.continueWithoutLocation")}
        />
      )}

      {!busy && stage === STAGE_BLOCKED && (
        <LocationError
          desc={
            blockedReason === "denied"
              ? locationDeniedDesc
              : t("deviceData.errors.locationUnavailable")
          }
          buttonText={
            blockedReason === "denied"
              ? t("deviceData.errors.refresh")
              : t("deviceData.errors.tryAgain")
          }
          tryAgain={blockedReason === "denied" ? reloadPage : requestLocation}
          onContinue={proceedWithoutLocation}
          continueText={t("deviceData.errors.continueWithoutLocation")}
        />
      )}
    </div>
  );
};

export default DeviceData;
